import { consume } from "@lit/context";
import { Task } from "@lit/task";
import { AbsenceStatus, DASHBOARD_CONFIG, dateAdd } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { enabledFeatures, type EnabledFeatures } from "../../../../contexts/features-context";
import "../../../../elements/absence-dialog";
import { app } from "../../../../init";
import { shared } from "../../../../styles";
import { getFixedOrCalculatedCommonWorkDays } from "../../utils/common-workdays";
import "../employee-absence-list";
import type { EmployeeAbsenceListItem } from "../employee-absence-list";
import "./base-tile";

@customElement("ptc-dashboard-tile-sick-leave")
export class DashboardTileSickLeave extends LitElement {
    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.sickLeaveRequests.size;

    @state()
    from: DateString = new Date().toISOString() as DateString;

    @consume({ context: enabledFeatures, subscribe: true })
    features: EnabledFeatures;

    @state()
    to: DateString = dateAdd(new Date().toISOString() as DateString, {
        years: 1,
    }) as DateString;

    private _absenceTask = new Task(this, {
        task: async ([api]) => {
            const absences = await api
                .getAbsences({
                    from: this.from,
                    to: this.to,
                    status: [AbsenceStatus.Requested],
                })
                .then((absences) =>
                    absences.filter(
                        (absence) => absence.status === AbsenceStatus.Requested && absence.type.includes("sick")
                    )
                );

            const groupedAbsences = Object.groupBy(absences, (absence) => absence.employeeId);

            const employeeAbsenceList: EmployeeAbsenceListItem[] = (
                await Promise.all(
                    Object.entries(groupedAbsences).map(async ([employeeId, absences = []]) => {
                        const employee = app.getEmployee(Number(employeeId));
                        if (!employee) {
                            return null;
                        }

                        const commonWorkDays = await getFixedOrCalculatedCommonWorkDays(app.api, employee);

                        return {
                            employee,
                            absences,
                            commonWorkDays,
                        };
                    })
                )
            ).filter((entry) => entry !== null && entry.employee !== null) as EmployeeAbsenceListItem[]; // @TODO: remove type cast when removing beta flag

            return employeeAbsenceList;
        },

        args: () => [app.api, this.from, this.to] as const, // @TODO: is app.api needed?
        autoRun: true,
    });

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }
        `,
    ];

    render() {
        return html`
            <ptc-dashboard-tile
                style="--tile-icon-color: var(--orange)"
                label=${DASHBOARD_CONFIG.tiles.sickLeaveRequests.label}
                .subLabel=${"Alle"}
                icon="icon.time-entry.sick"
                .size=${this.size}
                tileType="sickLeaveRequests"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                ${this._absenceTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-ui-spinner
                            class="double-padded place-self-center"
                            active
                            style="font-size: 1rem"
                        ></ptc-ui-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">
                            Krankmeldungen konnten nicht geladen werden!
                        </div>`,
                    complete: (absences) => {
                        return html`<ptc-employee-absence-list
                            style="--absence-duration-color: var(--orange)"
                            .employeeAbsences=${absences}
                        >
                            <div slot="empty-message" class="double-padded text-centering subtle place-self-center">
                                <span class="emoji huger">💪</span>
                                <p>Alle Krankmeldungen bearbeitet</p>
                            </div>
                        </ptc-employee-absence-list>`;
                    },
                })}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-sick-leave": DashboardTileSickLeave;
    }
}
